import { __rest, __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { queryOptions, useQuery } from '@tanstack/react-query';

var StoreBrandingType;
(function (StoreBrandingType) {
    StoreBrandingType["CustomDTCStore"] = "CustomDTCStore";
    StoreBrandingType["CustomStore"] = "CustomStore";
    StoreBrandingType["BrandStore"] = "BrandStore";
    StoreBrandingType["UniversalStore"] = "UniversalStore";
})(StoreBrandingType || (StoreBrandingType = {}));
// Constants
var CachedQueryKeys;
(function (CachedQueryKeys) {
    CachedQueryKeys["AppConfig"] = "appConfig";
    CachedQueryKeys["AppTheme"] = "appTheme";
    CachedQueryKeys["UserCart"] = "userCart";
    CachedQueryKeys["BrandAppConfigs"] = "brandAppConfigs";
    CachedQueryKeys["BrandClub"] = "brandClub";
    CachedQueryKeys["DTCProduct"] = "dtcProduct";
    CachedQueryKeys["SiteProduct"] = "siteProduct";
    CachedQueryKeys["UserProfile"] = "userProfile";
    CachedQueryKeys["ThemeSetup"] = "themeSetup";
    CachedQueryKeys["SuggestedQueries"] = "suggestedQueries";
})(CachedQueryKeys || (CachedQueryKeys = {}));
const THIRTY_MINUTES_IN_MS = 1000 * 60 * 30;
const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;
// Utility functions
// This logic is a little bit flaky as it is based on the order of the scripts being loaded in the DOM
const getHostDomain = () => {
    if (window.location.hostname.includes('brandclub.com')) {
        return window.location.origin;
    }
    // Get the current script element
    const scriptElement = document.currentScript ||
        (() => {
            const scripts = document.getElementsByTagName('script');
            return scripts[scripts.length - 1];
        })();
    // Get the script's source URL
    const scriptSrc = scriptElement.src;
    // Create a URL object to easily extract the domain
    const scriptURL = new URL(scriptSrc);
    // Extract the domain name
    return `https://${scriptURL.hostname}`;
};
const getCommonRequestHeaders = () => {
    return {
    // "X-Client-AppId": ConfigUtils.getWebAppId(), // TODO: Temporarily do not send this header
    };
};
// API functions
const getAppConfig = (signal) => __awaiter(void 0, void 0, void 0, function* () {
    const HOST_DOMAIN = getHostDomain();
    const DOMAIN = `${HOST_DOMAIN}` + '/api-rewards';
    const res = yield fetch(DOMAIN + '/config/webApp/getAppConfig', {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        return res.json();
    }
    else {
        throw new Error('Error fetching app config');
    }
});
const getBrandAppConfigs = (signal) => __awaiter(void 0, void 0, void 0, function* () {
    const HOST_DOMAIN = getHostDomain();
    const DOMAIN = `${HOST_DOMAIN}` + '/api-rewards';
    const res = yield fetch(DOMAIN + '/brandapp/brandAppConfig', {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        return res.json();
    }
    else {
        throw new Error('Error fetching brand app configs');
    }
});
const getSuggestedQueries = (signal, brandId) => __awaiter(void 0, void 0, void 0, function* () {
    const HOST_DOMAIN = getHostDomain();
    const DOMAIN = `${HOST_DOMAIN}` + '/api-rewards';
    const url = new URL(DOMAIN + '/concierge/suggestedQueries');
    if (brandId) {
        url.searchParams.set('brandId', brandId.toString());
    }
    const res = yield fetch(url.toString(), {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, getCommonRequestHeaders()),
        signal,
    });
    if (res.ok) {
        const data = yield res.json();
        return data;
    }
    else {
        throw new Error('Error fetching suggested queries');
    }
});
// Query options
const brandAppConfigQueryOptions = () => {
    return queryOptions({
        queryKey: [CachedQueryKeys.BrandAppConfigs],
        queryFn: ({ signal }) => getBrandAppConfigs(signal),
        // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
        staleTime: THIRTY_MINUTES_IN_MS,
    });
};
const appConfigQueryOptions = () => {
    return queryOptions({
        queryKey: [CachedQueryKeys.AppConfig],
        queryFn: ({ signal }) => getAppConfig(signal),
        // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
        staleTime: THIRTY_MINUTES_IN_MS,
    });
};
const suggestedQueriesQueryOptions = (brandId) => {
    return queryOptions({
        queryKey: [CachedQueryKeys.SuggestedQueries, brandId],
        queryFn: ({ signal }) => getSuggestedQueries(signal, brandId),
        // Suggested queries can be cached for a shorter time since they might change more frequently
        staleTime: FIVE_MINUTES_IN_MS,
    });
};
// Hooks
const useAppConfig = (args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, appConfigQueryOptions()), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};
const useDomainConfig = (args) => {
    const _a = useAppConfig(args), { data: appConfig } = _a, rest = __rest(_a, ["data"]);
    return Object.assign({ data: appConfig === null || appConfig === void 0 ? void 0 : appConfig.domainConfig }, rest);
};
const useBrandAppConfig = (args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, brandAppConfigQueryOptions()), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};
const useSuggestedQueries = (brandId, args) => {
    return useQuery(Object.assign(Object.assign(Object.assign({}, suggestedQueriesQueryOptions(brandId)), args), { select: args === null || args === void 0 ? void 0 : args.select }));
};

export { CachedQueryKeys, FIVE_MINUTES_IN_MS, StoreBrandingType, THIRTY_MINUTES_IN_MS, appConfigQueryOptions, brandAppConfigQueryOptions, suggestedQueriesQueryOptions, useAppConfig, useBrandAppConfig, useDomainConfig, useSuggestedQueries };
//# sourceMappingURL=appConfigQueryOptions.js.map
